import {
  ApiClient,
  ApiClientContext,
  ApiClientOptions,
  PaginatedRouteOptions,
  RouteOptions,
  defaultAxiosConfig,
} from '@qualifio/api-client';
import { AxiosInstance } from 'axios';
import {
  FontAttributes,
  GetTemplateWidgetsResponse,
  SaveTemplateVersionBody,
  TemplateVersionAttributes,
  UpdateTemplateNameBody,
} from './template.types';

export interface GetFontsOptions extends PaginatedRouteOptions {
  withDeleted?: boolean;
}

export interface getTemplatesQueryParams {
  size?: number;
  skip?: number;
}

export class TemplateApiClient extends ApiClient {
  static serviceName = 'template-api';

  constructor(http?: AxiosInstance, context?: ApiClientContext, options?: ApiClientOptions) {
    super('/api/template', http, context, options);
  }

  async getFonts(accountId: string, options?: GetFontsOptions): Promise<FontAttributes[]> {
    const { data } = await this.http.get(`/accounts/${accountId}/fonts`, {
      ...defaultAxiosConfig(options),
      params: { withDeleted: options?.withDeleted, ...options?.pagination },
    });
    return data;
  }

  async addFont(
    programId: string,
    font: FontAttributes,
    options?: RouteOptions,
  ): Promise<FontAttributes> {
    const { data } = await this.http.post(
      `/accounts/${programId}/fonts`,
      font,
      defaultAxiosConfig(options),
    );
    return data;
  }

  async updateFont(font: FontAttributes, options?: RouteOptions): Promise<FontAttributes> {
    const { data } = await this.http.put(`/fonts/${font.id}`, font, defaultAxiosConfig(options));
    return data;
  }

  async deleteFont(fontId: string, options?: RouteOptions): Promise<FontAttributes> {
    const { data } = await this.http.delete(`/fonts/${fontId}`, defaultAxiosConfig(options));
    return data;
  }

  async getLastTemplates(
    programId: string,
    options?: RouteOptions<getTemplatesQueryParams>,
  ): Promise<TemplateVersionAttributes[]> {
    const { data } = await this.http.get(
      `/programs/${programId}/last-templates`,
      defaultAxiosConfig(options),
    );
    return data;
  }

  async getTemplateById(
    templateId: string,
    options?: RouteOptions,
  ): Promise<TemplateVersionAttributes> {
    const { data } = await this.http.get(`/templates/${templateId}`, defaultAxiosConfig(options));
    return data;
  }

  async getTemplateWidgetsByType(
    templateId: string,
    widgetType: string,
    options?: RouteOptions,
  ): Promise<GetTemplateWidgetsResponse> {
    const { data } = await this.http.get(`/templates/${templateId}/widgets`, {
      ...defaultAxiosConfig(options),
      params: { widget_type: widgetType },
    });
    return data;
  }

  async saveTemplate(
    programId: string,
    template: SaveTemplateVersionBody,
    options?: RouteOptions,
  ): Promise<TemplateVersionAttributes> {
    const { data } = await this.http.put(
      `/programs/${programId}/save-template`,
      template,
      defaultAxiosConfig(options),
    );
    return data;
  }

  async updateTemplateName(
    templateId: string,
    body: UpdateTemplateNameBody,
    options?: RouteOptions,
  ): Promise<TemplateVersionAttributes> {
    const { data } = await this.http.put(
      `/templates/${templateId}`,
      body,
      defaultAxiosConfig(options),
    );
    return data;
  }

  async getScheduledTemplate(
    programId: string,
    options?: RouteOptions,
  ): Promise<TemplateVersionAttributes | null> {
    const { data } = await this.http.get(
      `/programs/${programId}/scheduled-template`,
      defaultAxiosConfig(options),
    );
    return data;
  }

  async scheduleTemplate(
    programId: string,
    templateId: string,
    publicationDate: string,
    options?: RouteOptions,
  ): Promise<TemplateVersionAttributes> {
    const { data } = await this.http.put(
      `/programs/${programId}/schedule-template-publication`,
      { templateId, publicationDate },
      defaultAxiosConfig(options),
    );
    return data;
  }

  async cancelScheduleTemplate(
    programId: string,
    options?: RouteOptions,
  ): Promise<TemplateVersionAttributes> {
    const { data } = await this.http.delete(
      `/programs/${programId}/template-publication-schedule`,
      defaultAxiosConfig(options),
    );
    return data;
  }
}
